import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { validateFoodBookingTime } from "@/lib/kitchen-hours";
import { requiresEmailBooking } from "@/lib/booking-policy";

function isSameDayDublin(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  // Format both in Europe/Dublin to compare dates
  const fmt = (x: Date) =>
    x.toLocaleDateString("en-IE", { timeZone: "Europe/Dublin" });
  return fmt(d) === fmt(now);
}

function hoursUntilDublin(iso: string): number {
  const d = new Date(iso).getTime();
  const now = Date.now();
  return (d - now) / 1000 / 60 / 60;
}

/**
 * Booking type values.
 */
const BookingTypeEnum = z.enum([
  "pool_table",
  "food_and_drinks",
  "sports_viewing",
  "darts",
  "group_booking",
  "karaoke",
]);

const CreateReservationInput = z.object({
  submission_key: z.string().uuid(),
  booking_type: BookingTypeEnum,
  starts_at: z.string().datetime({ offset: true }),
  duration_minutes: z.number().int().min(60).max(240),
  party_size: z.number().int().min(1).max(80),
  preferred_area: z.string().max(120).optional().nullable(),
  notes: z.string().max(500).optional().nullable(),
  guest: z.object({
    full_name: z.string().trim().min(2).max(120),
    email: z.string().trim().email().max(200),
    phone: z.string().trim().min(6).max(32),
    marketing_consent: z.boolean().default(false),
  }),
  agreed_terms: z.literal(true),
  agreed_house_rules: z.literal(true),
});


type CreateReservationOutput = {
  reference: string;
  status:
    | "confirmed"
    | "pending_approval"
    | "pending_payment"
    | "unavailable";
};

function isPeakTime(d: Date): boolean {
  const day = d.getUTCDay(); // 0 Sun .. 6 Sat
  // Treat Fri (5) & Sat (6) as peak
  return day === 5 || day === 6;
}

const SPORTS_VIEW_TABLE_LABELS = [
  "Table 2",
  "Table 3",
  "Table 10",
  "Table 11",
  "Table 12",
  "Table 13",
  "Table 14",
  "Table 15",
  "Table 16",
  "Table 17",
  "Table 18",
  "Table 21",
  "Table 22",
  "Table 23",
  "Table 24",
  "Table 25",
];

// Shared availability check lives in availability.server.ts so every booking
// channel (website, WhatsApp, staff) uses exactly the same rules.
async function findAvailableTable(
  ...args: Parameters<typeof import("@/lib/availability.server")["findAvailableTable"]>
): Promise<string | null> {
  const m = await import("@/lib/availability.server");
  return m.findAvailableTable(...args);
}

export const createReservation = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => CreateReservationInput.parse(data))
  .handler(async ({ data }): Promise<CreateReservationOutput> => {
    if (requiresEmailBooking(data.booking_type, data.party_size)) {
      throw new Error("Groups over 10 and Karaoke Terrace private hire must email bookings@plunkets.ie.");
    }
    // 1) Same-day bookings need at least 2 hours notice (Dublin time)
    if (isSameDayDublin(data.starts_at) && hoursUntilDublin(data.starts_at) < 2) {
      throw new Error("Same-day bookings must be made at least 2 hours in advance.");
    }

    // 1b) Kitchen cutoff for food bookings
    const kitchenErr = validateFoodBookingTime(data.booking_type, data.starts_at);
    if (kitchenErr) throw new Error(kitchenErr);


    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );




    // 2) A retried request with the same client-generated key must return the
    // original booking before changing any guest details.
    const { data: priorSubmission } = await supabaseAdmin
      .from("reservations")
      .select("reference,status")
      .eq("submission_key", data.submission_key)
      .maybeSingle();
    if (priorSubmission) {
      return {
        reference: priorSubmission.reference,
        status: priorSubmission.status as CreateReservationOutput["status"],
      };
    }

    // 3) Upsert guest by phone
    const { data: existingGuest } = await supabaseAdmin
      .from("guests")
      .select("*")
      .eq("phone", data.guest.phone)
      .maybeSingle();

    let guestId = existingGuest?.id as string | undefined;
    if (!guestId) {
      const { data: g, error: gErr } = await supabaseAdmin
        .from("guests")
        .insert({
          full_name: data.guest.full_name,
          email: data.guest.email,
          phone: data.guest.phone,
          phone_verified: true,
          marketing_consent: data.guest.marketing_consent,
        })
        .select("id, status")
        .single();
      if (gErr || !g) throw new Error("Failed to create guest");
      guestId = g.id;
    } else {
      await supabaseAdmin
        .from("guests")
        .update({
          full_name: data.guest.full_name,
          email: data.guest.email,
          phone_verified: true,
          marketing_consent: data.guest.marketing_consent,
        })
        .eq("id", guestId);
    }

    if (!guestId) throw new Error("Failed to identify guest");
    const resolvedGuestId = guestId;

    // 4) Load rules & guest status
    const { data: rules } = await supabaseAdmin
      .from("booking_rules")
      .select("*")
      .eq("id", 1)
      .single();
    const { data: guest } = await supabaseAdmin
      .from("guests")
      .select("status")
      .eq("id", resolvedGuestId)
      .single();
    void guest;

    // 4b) Friendly pre-check for an existing booking. The database trigger
    // below remains the atomic authority when simultaneous requests race.
    {
      const { data: sameSlot } = await supabaseAdmin
        .from("reservations")
        .select("id,reference,status")
        .eq("guest_id", resolvedGuestId)
        .eq("starts_at", data.starts_at)
        .eq("booking_type", data.booking_type)
        .not("status", "in", "(cancelled,no_show)")
        .limit(1)
        .maybeSingle();
      if (sameSlot) {
        return {
          reference: sameSlot.reference as string,
          status: sameSlot.status as CreateReservationOutput["status"],
        };
      }
    }

    // No deposits: pool/darts bookings never wait on payment.
    void rules;
    let assignedTableId: string | null = null;
    let assignedTableIds: string[] = [];

    // 5) Eligible parties confirm only when a free resource is available.
    const status = "confirmed" as const;
    {
      const { findAvailableTables } = await import("@/lib/availability.server");
      assignedTableIds = await findAvailableTables(
        supabaseAdmin,
        data.booking_type,
        data.starts_at,
        data.duration_minutes,
        data.party_size,
      );

      assignedTableId = assignedTableIds[0] ?? null;

      // No free table/resource at this time → never double-book.
      // Put the guest on the waitlist instead of creating a reservation.
      if (!assignedTableId) {
        const { error: waitlistError } = await supabaseAdmin.from("waitlist").insert({
          guest_id: resolvedGuestId,
          booking_type: data.booking_type,
          desired_at: data.starts_at,
          party_size: data.party_size,
          notes: data.notes ?? null,
          source: "website",
        });
        if (waitlistError) throw new Error("Could not join the waitlist. Please try again.");
        return { reference: "WAITLIST", status: "unavailable" as const };
      }
    }


    const { confirmBooking } = await import("@/lib/confirm-booking.server");
    const reservation = await confirmBooking(supabaseAdmin, {
      booking_type: data.booking_type, guest_id: resolvedGuestId,
      submission_key: data.submission_key, starts_at: data.starts_at,
      duration_minutes: data.duration_minutes, party_size: data.party_size,
      preferred_area: data.preferred_area ?? null, notes: data.notes ?? null,
      source: "website",
    }, assignedTableIds);

    // 6) Email the guest + the bookings inbox (never block the booking on this)
    {
      let tableLabel: string | undefined;
      if (assignedTableId) {
        const { data: t } = await supabaseAdmin
          .from("tables")
          .select("label")
          .eq("id", assignedTableId)
          .maybeSingle();
        tableLabel = t?.label ?? undefined;
      }
      const { sendBookingEmail } = await import("@/lib/booking-emails.server");
      await sendBookingEmail({
        template: "booking-confirmation",
        to: data.guest.email ?? "",
        reservationId: reservation.id,
        reference: reservation.reference,
        fullName: data.guest.full_name,
        startsAt: data.starts_at,
        partySize: data.party_size,
        bookingType: data.booking_type,
        durationMinutes: data.duration_minutes,
        tableLabel: tableLabel ?? null,
        confirmed: status === "confirmed",
      });
    }

    return {
      reference: reservation.reference,
      status: status,
    };
  });

function formatDublin(iso: string) {
  return new Date(iso).toLocaleString("en-IE", {
    timeZone: "Europe/Dublin",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}


/* ---------- PHONE VERIFICATION (WhatsApp Cloud API) ----------
 * Sends the 6-digit code via Meta's WhatsApp Cloud API using an
 * approved authentication template. Falls back to a dev-only code
 * surfaced in the UI when WhatsApp secrets are not yet configured,
 * so the booking flow keeps working in preview.
 *
 * Required secrets (set later in Project Settings):
 *   - WHATSAPP_ACCESS_TOKEN       (permanent System User token)
 *   - WHATSAPP_PHONE_NUMBER_ID    (sender phone number ID)
 *   - WHATSAPP_TEMPLATE_NAME      (approved authentication template name)
 *   - WHATSAPP_TEMPLATE_LANG      (optional, default "en")
 */

async function sendWhatsAppCode(
  phone: string,
  code: string,
): Promise<{ sent: boolean; error?: string }> {
  const token = process.env.WHATSAPP_ACCESS_TOKEN;
  const phoneNumberId = process.env.WHATSAPP_PHONE_NUMBER_ID;
  const template = process.env.WHATSAPP_TEMPLATE_NAME;
  const lang = process.env.WHATSAPP_TEMPLATE_LANG || "en";
  if (!token || !phoneNumberId || !template) {
    return { sent: false, error: "WhatsApp not configured" };
  }
  const to = phone.replace(/[^\d]/g, "");
  try {
    const res = await fetch(
      `https://graph.facebook.com/v20.0/${phoneNumberId}/messages`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messaging_product: "whatsapp",
          to,
          type: "template",
          template: {
            name: template,
            language: { code: lang },
            components: [
              {
                type: "body",
                parameters: [{ type: "text", text: code }],
              },
              {
                type: "button",
                sub_type: "url",
                index: "0",
                parameters: [{ type: "text", text: code }],
              },
            ],
          },
        }),
      },
    );
    if (!res.ok) {
      const body = await res.text();
      console.error("WhatsApp send failed", res.status, body);
      return { sent: false, error: `WhatsApp error ${res.status}` };
    }
    return { sent: true };
  } catch (e) {
    console.error("WhatsApp send threw", e);
    return { sent: false, error: (e as Error).message };
  }
}

export const startPhoneVerification = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ phone: z.string().trim().min(6).max(32) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { createHash } = await import("crypto");

    if (
      !process.env.WHATSAPP_ACCESS_TOKEN ||
      !process.env.WHATSAPP_PHONE_NUMBER_ID ||
      !process.env.WHATSAPP_TEMPLATE_NAME
    ) {
      throw new Error(
        "Phone verification is temporarily unavailable. Please contact the pub directly.",
      );
    }

    const code = String(Math.floor(100000 + Math.random() * 900000));
    const expires = new Date(Date.now() + 10 * 60 * 1000).toISOString();
    const codeHash = createHash("sha256").update(code).digest("hex");

    const { data: row, error } = await supabaseAdmin
      .from("phone_verifications")
      .insert({
        phone: data.phone,
        code_hash: codeHash,
        expires_at: expires,
      })
      .select("id")
      .single();
    if (error || !row) throw new Error("Could not start verification");

    const send = await sendWhatsAppCode(data.phone, code);
    if (!send.sent) {
      throw new Error(
        "Could not send WhatsApp code. Check the number is on WhatsApp.",
      );
    }

    return {
      id: row.id,
      sent_via: "whatsapp" as const,
    };
  });


export const confirmPhoneVerification = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        code: z.string().trim().min(4).max(8),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: pv } = await supabaseAdmin
      .from("phone_verifications")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (!pv) throw new Error("Verification not found");
    if (new Date(pv.expires_at) < new Date()) throw new Error("Code expired");
    if (pv.attempts >= 5) throw new Error("Too many attempts");

    const { createHash } = await import("crypto");
    const candidateHash = createHash("sha256").update(data.code).digest("hex");
    const ok = pv.code_hash === candidateHash;
    await supabaseAdmin
      .from("phone_verifications")
      .update({
        attempts: pv.attempts + 1,
        verified: ok ? true : pv.verified,
      })
      .eq("id", data.id);
    if (!ok) throw new Error("Incorrect code");
    return { ok: true };
  });

export const getReservationByReference = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ reference: z.string().min(4).max(32) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: r } = await supabaseAdmin
      .from("reservations")
      .select(
        "reference,booking_type,starts_at,duration_minutes,party_size,status,deposit_required,deposit_amount_cents,preferred_area,notes,table:tables(label,capacity,zone:zones(name))",
      )
      .eq("reference", data.reference)
      .maybeSingle();
    return r;
  });

/* ---------- ADMIN ---------- */

function startOfTodayDublinISO(): string {
  const now = new Date();
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Dublin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  // Real Dublin offset at that moment (IST in summer, GMT in winter) so the
  // day boundary never slips an hour when the clocks change.
  const probe = new Date(`${ymd}T12:00:00Z`);
  const offsetMin =
    (new Date(probe.toLocaleString("en-US", { timeZone: "Europe/Dublin" })).getTime() -
      new Date(probe.toLocaleString("en-US", { timeZone: "UTC" })).getTime()) /
    60000;
  return new Date(new Date(`${ymd}T00:00:00Z`).getTime() - offsetMin * 60000).toISOString();
}

export const adminListReservations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({ includePast: z.boolean().optional() })
      .optional()
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    let query = context.supabase
      .from("reservations")
      .select(
        "id,reference,booking_type,source,starts_at,duration_minutes,party_size,status,created_at,deposit_required,deposit_amount_cents,preferred_area,notes,table_id,reservation_tables(table_id),guest:guests(full_name,phone,email,status)",
      )
      .order("starts_at", { ascending: true })
      .limit(500);

    // Default view hides reservations from before today so staff don't have to
    // scroll through months of old bookings to reach today's list.
    if (!data?.includePast) {
      query = query.gte("starts_at", startOfTodayDublinISO());
    }

    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const adminUpdateReservationStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum([
          "confirmed",
          "pending_approval",
          "pending_payment",
          "unavailable",
          "cancelled",
          "no_show",
          "checked_in",
          "seated",
          "completed",
        ]),
        reason: z.string().max(300).optional(),
        table_id: z.string().uuid().nullable().optional(),
        /** Multiple tables for large groups. Takes precedence over table_id. */
        table_ids: z.array(z.string().uuid()).max(12).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: current } = await context.supabase
      .from("reservations")
      .select("status, table_id, booking_type")
      .eq("id", data.id)
      .single();

    const { data: currentLinks } = await context.supabase
      .from("reservation_tables")
      .select("table_id")
      .eq("reservation_id", data.id);

    const prevTableIds = Array.from(
      new Set([
        ...((currentLinks ?? []).map((l) => l.table_id as string)),
        ...(current?.table_id ? [current.table_id as string] : []),
      ]),
    );

    let nextTableIds: string[] | null = null; // null = unchanged
    if (data.table_ids !== undefined) {
      nextTableIds = Array.from(new Set(data.table_ids));
    } else if (data.table_id !== undefined) {
      nextTableIds = data.table_id ? [data.table_id] : [];
    }

    const effectiveTableIds = nextTableIds ?? prevTableIds;

    const updates: { status: typeof data.status; table_id?: string | null } = {
      status: data.status,
    };
    if (nextTableIds !== null) updates.table_id = nextTableIds[0] ?? null;
    const { error } = await context.supabase
      .from("reservations")
      .update(updates)
      .eq("id", data.id);
    if (error) throw new Error(error.message);

    // Keep the reservation ⇄ tables links in sync
    if (nextTableIds !== null) {
      await context.supabase
        .from("reservation_tables")
        .delete()
        .eq("reservation_id", data.id);
      if (nextTableIds.length) {
        await context.supabase.from("reservation_tables").insert(
          nextTableIds.map((tid) => ({ reservation_id: data.id, table_id: tid })),
        );
      }
    }

    // ---- Floor plan sync ----
    type TableStatus =
      | "available"
      | "reserved"
      | "occupied"
      | "out_of_service";
    let nextTableStatus: TableStatus | null = null;
    if (data.status === "checked_in" || data.status === "seated") {
      nextTableStatus = "occupied";
    } else if (data.status === "confirmed") {
      nextTableStatus = "reserved";
    } else if (
      data.status === "completed" ||
      data.status === "cancelled" ||
      data.status === "no_show" ||
      data.status === "unavailable"
    ) {
      nextTableStatus = "available";
    }

    // Free any table that is no longer assigned to this booking
    const released = prevTableIds.filter((id) => !effectiveTableIds.includes(id));
    if (released.length) {
      await context.supabase
        .from("tables")
        .update({ status: "available", status_changed_at: new Date().toISOString() } as never)
        .in("id", released);
    }

    if (effectiveTableIds.length && nextTableStatus) {
      await context.supabase
        .from("tables")
        .update({ status: nextTableStatus, status_changed_at: new Date().toISOString() } as never)
        .in("id", effectiveTableIds);
    }

    await context.supabase.from("reservation_status_history").insert({
      reservation_id: data.id,
      from_status: current?.status ?? null,
      to_status: data.status,
      changed_by: context.userId,
      reason: data.reason ?? null,
    });
    await context.supabase.from("audit_log").insert({
      actor_id: context.userId,
      action: "reservation.status_changed",
      entity_type: "reservation",
      entity_id: data.id,
      meta: {
        to: data.status,
        reason: data.reason ?? null,
        table_ids: effectiveTableIds,
        table_status: nextTableStatus,
      },
    });

    // Tell the guest once their table has actually been assigned/confirmed.
    if (data.status === "confirmed" && effectiveTableIds.length) {
      const { data: full } = await context.supabase
        .from("reservations")
        .select(
          "reference,starts_at,duration_minutes,party_size,booking_type,guest:guests(full_name,email)",
        )
        .eq("id", data.id)
        .maybeSingle();
      const guest = (full as { guest?: { full_name?: string; email?: string } } | null)?.guest;
      if (full) {
        const { data: ts } = await context.supabase
          .from("tables")
          .select("label")
          .in("id", effectiveTableIds);
        const { sendBookingEmail } = await import("@/lib/booking-emails.server");
        await sendBookingEmail({
          template: "table-confirmed",
          to: guest?.email ?? "",
          reservationId: data.id,
          reference: full.reference as string,
          fullName: guest?.full_name ?? null,
          startsAt: full.starts_at as string,
          partySize: full.party_size as number,
          bookingType: full.booking_type as string,
          durationMinutes: full.duration_minutes as number,
          tableLabel: (ts ?? []).map((t) => t.label).join(", ") || null,
        });
      }
    }

    // Instagram bookings: tell the customer in their DM when staff confirm/cancel.
    if (current?.status !== data.status) {
      const { notifyInstagramBooking } = await import("@/lib/instagram-notify.server");
      await notifyInstagramBooking(data.id, data.status);
    }

    return { ok: true };
  });


/** List tables for admin assignment dropdowns. */
export const adminListTables = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("tables")
      .select("id,label,capacity,resource_type,status")
      .order("label");
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getMyRole = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    return { roles: (data ?? []).map((r) => r.role as string) };
  });

export const adminDashboardSummary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const today = new Date();
    const start = new Date(today.toISOString().slice(0, 10) + "T00:00:00Z");
    const end = new Date(start.getTime() + 24 * 3600 * 1000);
    const { data: todays } = await context.supabase
      .from("reservations")
      .select("id,party_size,status,booking_type")
      .gte("starts_at", start.toISOString())
      .lt("starts_at", end.toISOString());

    const list = todays ?? [];
    const covers = list.reduce((sum, r) => sum + (r.party_size || 0), 0);
    const pending = list.filter((r) => r.status === "pending_approval").length;
    const pendingPay = list.filter((r) => r.status === "pending_payment").length;
    const noShow = list.filter((r) => r.status === "no_show").length;
    const poolToday = list.filter((r) => r.booking_type === "pool_table").length;

    const { count: waitlistCount } = await context.supabase
      .from("waitlist")
      .select("*", { count: "exact", head: true });

    return {
      todayCount: list.length,
      covers,
      pendingApproval: pending,
      pendingPayment: pendingPay,
      noShows: noShow,
      poolToday,
      waitlistCount: waitlistCount ?? 0,
    };
  });

/** Staff-created (walk-in / phone) reservation. Bypasses public booking windows. */
const AdminCreateReservationInput = z.object({
  submission_key: z.string().uuid(),
  booking_type: BookingTypeEnum,
  starts_at: z.string().datetime({ offset: true }),
  duration_minutes: z.number().int().min(30).max(300),
  party_size: z.number().int().min(1).max(80),
  preferred_area: z.string().max(120).optional().nullable(),
  notes: z.string().max(500).optional().nullable(),
  table_id: z.string().uuid().nullable().optional(),
  /** Multiple tables for large groups. Takes precedence over table_id. */
  table_ids: z.array(z.string().uuid()).max(12).optional(),

  status: z.literal("confirmed").default("confirmed"),
  source: z.enum(["admin", "phone", "instagram"]).default("admin"),
  /** Name of the staff member taking the manual booking. */
  booked_by_name: z.string().trim().min(2).max(80),
  social_conversation_id: z.string().uuid().nullable().optional(),
  guest: z.object({
    full_name: z.string().trim().min(2).max(120),
    email: z.string().trim().email().max(200).optional().or(z.literal("")),
    phone: z.string().trim().min(6).max(32),
  }),
});

export const adminCreateReservation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => AdminCreateReservationInput.parse(data))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: priorSubmission } = await supabase
      .from("reservations")
      .select("id,reference,status")
      .eq("submission_key", data.submission_key)
      .maybeSingle();
    if (priorSubmission) {
      return {
        id: priorSubmission.id,
        reference: priorSubmission.reference,
        status: priorSubmission.status,
        table_ids: [] as string[],
        duplicate: true as const,
      };
    }

    const { data: existing } = await supabase
      .from("guests")
      .select("id")
      .eq("phone", data.guest.phone)
      .maybeSingle();

    let guestId = existing?.id as string | undefined;
    if (!guestId) {
      const { data: g, error: gErr } = await supabase
        .from("guests")
        .insert({
          full_name: data.guest.full_name,
          email: data.guest.email || null,
          phone: data.guest.phone,
        })
        .select("id")
        .single();
      if (gErr || !g) throw new Error(gErr?.message ?? "Failed to create guest");
      guestId = g.id;
    } else {
      await supabase
        .from("guests")
        .update({
          full_name: data.guest.full_name,
          ...(data.guest.email ? { email: data.guest.email } : {}),
        })
        .eq("id", guestId);
    }

    const tableIds = Array.from(
      new Set(
        data.table_ids && data.table_ids.length
          ? data.table_ids
          : data.table_id
            ? [data.table_id]
            : [],
      ),
    );

    // Idempotency guard: the same guest, same slot and same booking type must
    // never be created twice (double-click / double submit / retry).
    const { data: dup } = await supabase
      .from("reservations")
      .select("id,reference,status")
      .eq("guest_id", guestId)
      .eq("starts_at", data.starts_at)
      .eq("booking_type", data.booking_type)
      .not("status", "in", "(cancelled,no_show)")
      .limit(1)
      .maybeSingle();
    if (dup) {
      return { id: dup.id, reference: dup.reference, status: dup.status, table_ids: tableIds, duplicate: true as const };
    }

    const { data: r, error } = await supabase
      .from("reservations")
      .insert({
        submission_key: data.submission_key,
        booking_type: data.booking_type,
        starts_at: data.starts_at,
        duration_minutes: data.duration_minutes,
        party_size: data.party_size,
        preferred_area: data.preferred_area ?? null,
        notes: data.notes ?? null,
        table_id: tableIds[0] ?? null,
        status: data.status,
        source: data.source,
        booked_by_name: data.booked_by_name,
        social_conversation_id: data.social_conversation_id ?? null,
        guest_id: guestId,
        agreed_terms: true,
        agreed_house_rules: true,
      })
      .select("id,reference,status")
      .single();
    if (error || !r) {
      if (error?.code === "23505") {
        const { data: winner } = await supabase
          .from("reservations")
          .select("id,reference,status")
          .eq("guest_id", guestId)
          .eq("starts_at", data.starts_at)
          .eq("booking_type", data.booking_type)
          .not("status", "in", "(cancelled,no_show)")
          .limit(1)
          .maybeSingle();
        if (winner) {
          return {
            id: winner.id,
            reference: winner.reference,
            status: winner.status,
            table_ids: tableIds,
            duplicate: true as const,
          };
        }
      }
      throw new Error(error?.message ?? "Failed to create reservation");
    }

    if (tableIds.length) {
      await supabase
        .from("reservation_tables")
        .insert(tableIds.map((tid) => ({ reservation_id: r.id, table_id: tid })));
    }

    await supabase.from("reservation_status_history").insert({
      reservation_id: r.id,
      to_status: r.status,
      changed_by: userId,
      reason: "Created manually by staff",
    });

    if (tableIds.length && data.status === "confirmed") {
      await supabase.from("tables").update({ status: "reserved", status_changed_at: new Date().toISOString() } as never).in("id", tableIds);
    }

    // Email the guest + bookings inbox (staff-created bookings included)
    {
      let tableLabel: string | null = null;
      if (tableIds.length) {
        const { data: ts } = await supabase
          .from("tables")
          .select("label")
          .in("id", tableIds);
        tableLabel = (ts ?? []).map((t) => t.label).join(", ") || null;
      }
      const { sendBookingEmail } = await import("@/lib/booking-emails.server");
      await sendBookingEmail({
        template: "booking-confirmation",
        to: data.guest.email ?? "",
        reservationId: r.id,
        reference: r.reference,
        fullName: data.guest.full_name,
        startsAt: data.starts_at,
        partySize: data.party_size,
        bookingType: data.booking_type,
        durationMinutes: data.duration_minutes,
        tableLabel,
        confirmed: r.status === "confirmed",
      });
    }

    if (data.social_conversation_id) {
      const { notifyInstagramBooking } = await import("@/lib/instagram-notify.server");
      await notifyInstagramBooking(r.id, r.status);
    }

    return { id: r.id, reference: r.reference, status: r.status, table_ids: tableIds, duplicate: false as const };
  });

/** Edit the core details of an existing reservation (staff only). */
export const adminEditReservation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        booking_type: BookingTypeEnum,
        starts_at: z.string().datetime({ offset: true }),
        duration_minutes: z.number().int().min(30).max(300),
        party_size: z.number().int().min(1).max(80),
        notes: z.string().max(500).optional().nullable(),
        preferred_area: z.string().max(80).optional().nullable(),
        guest_full_name: z.string().trim().min(2).max(120).optional(),
        guest_phone: z.string().trim().min(6).max(32).optional(),
        guest_email: z.string().trim().email().max(160).optional().or(z.literal("")),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: existing, error: findErr } = await context.supabase
      .from("reservations")
      .select("guest_id")
      .eq("id", data.id)
      .maybeSingle();
    if (findErr) throw new Error(findErr.message);

    const { error } = await context.supabase
      .from("reservations")
      .update({
        booking_type: data.booking_type,
        starts_at: data.starts_at,
        duration_minutes: data.duration_minutes,
        party_size: data.party_size,
        notes: data.notes ?? null,
        preferred_area: data.preferred_area ?? null,
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);

    // Guest details (name / phone / email) are editable from the same dialog.
    if (existing?.guest_id && (data.guest_full_name || data.guest_phone || data.guest_email !== undefined)) {
      const guestPatch: { full_name?: string; phone?: string; email?: string | null } = {};
      if (data.guest_full_name) guestPatch.full_name = data.guest_full_name;
      if (data.guest_phone) guestPatch.phone = data.guest_phone;
      if (data.guest_email !== undefined) guestPatch.email = data.guest_email ? data.guest_email : null;
      if (Object.keys(guestPatch).length > 0) {
        const { error: gErr } = await context.supabase
          .from("guests")
          .update(guestPatch)
          .eq("id", existing.guest_id);
        if (gErr) throw new Error(gErr.message);
      }
    }


    await context.supabase.from("audit_log").insert({
      actor_id: context.userId,
      action: "reservation.edited",
      entity_type: "reservation",
      entity_id: data.id,
      meta: {
        starts_at: data.starts_at,
        party_size: data.party_size,
        duration_minutes: data.duration_minutes,
        booking_type: data.booking_type,
      },
    });
    return { ok: true };
  });

/** Recent bookings for the same phone — used to warn staff about duplicates. */
export const adminFindGuestBookings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ phone: z.string().trim().min(6).max(32) }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: guest } = await context.supabase
      .from("guests")
      .select("id")
      .eq("phone", data.phone)
      .maybeSingle();
    if (!guest) return [];
    const { data: rows } = await context.supabase
      .from("reservations")
      .select("id,reference,starts_at,party_size,booking_type,status,duration_minutes,notes")
      .eq("guest_id", guest.id)
      .gte("starts_at", startOfTodayDublinISO())
      .not("status", "in", "(cancelled,no_show,completed)")
      .order("starts_at", { ascending: true })
      .limit(10);
    return rows ?? [];
  });

/** Update only the notes of a reservation (used from Calendar & Floor plan). */
export const adminUpdateReservationNotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), notes: z.string().max(1000).nullable() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const notes = data.notes?.trim() || null;
    const { error } = await context.supabase.from("reservations").update({ notes }).eq("id", data.id);
    if (error) throw new Error(error.message);
    await context.supabase.from("audit_log").insert({
      actor_id: context.userId,
      action: "reservation.notes_updated",
      entity_type: "reservation",
      entity_id: data.id,
      meta: { notes },
    });
    return { ok: true, notes };
  });

/** Permanently remove a reservation (owner/manager only). */
export const adminDeleteReservation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: roleRows } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const roles = (roleRows ?? []).map((r) => r.role as string);
    if (!roles.some((r) => r === "owner" || r === "manager")) {
      throw new Error("Only owners and managers can delete bookings.");
    }

    const { data: current } = await context.supabase
      .from("reservations")
      .select("reference,starts_at,party_size,booking_type,guest_id")
      .eq("id", data.id)
      .single();

    await context.supabase.from("reservation_tables").delete().eq("reservation_id", data.id);
    await context.supabase.from("reservation_status_history").delete().eq("reservation_id", data.id);
    await context.supabase.from("payments").delete().eq("reservation_id", data.id);

    const { error } = await context.supabase.from("reservations").delete().eq("id", data.id);
    if (error) throw new Error(error.message);

    await context.supabase.from("audit_log").insert({
      actor_id: context.userId,
      action: "reservation.deleted",
      entity_type: "reservation",
      entity_id: data.id,
      meta: current ?? {},
    });

    return { ok: true };
  });
